import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'
import { sumBy } from 'lodash'

import type { PaymentStatus } from 'modules/payroll_bundle/components/common/components/ShowStatus'
import type { PayrollEmployeeResultsSummary } from 'modules/payroll_bundle/hooks/useEmployeeResultsSummaries'
import {
  DEFAULT_RESULT_CONCEPT,
  NOT_PAID,
  OVERPAID,
  PAID,
  PARTIALLY_PAID,
} from 'modules/payroll_bundle/lib/constants'
import { ResultConcepts, ResultConceptsType } from 'modules/payroll_bundle/lib/constants'
import { paymentItemLib } from 'modules/payroll_bundle/lib/paymentItem'
import type { Payments } from 'modules/payroll_bundle/store/collections/payments'
import type { Results } from 'modules/payroll_bundle/store/collections/results'

export type ResultsTableData = ResultRowData[]
export interface ResultRowData {
  id: number
  employee: ResultConceptItemTableRowFragment['employee']
  paymentType: ResultConceptsType
  // conceptItemHash is generated using the concept id and the policy period id. It is used to group result concept items by policy period.
  // Example: 'net:1' means that the result concept item is the 'net' result concept on policy period 1.
  conceptItems: { [conceptItemHash: string]: ResultConceptItemTableData }
  createdAt: string
}

export type ResultConceptItemTableData = {
  conceptId: number
  conceptName: DEFAULT_RESULT_CONCEPT | string
  amount: number | null
  default: boolean
  policyPeriodId: number
}

/**
 * Converts an array of result concepts into a table data structure. ResultConceptsRowData structure makes easier to show information on a table.
 *
 * Result concept items are grouped by employee id and payment type, so every row in the table is data related to result concepts of an employee on a specific payment
 * type.
 *
 * This means that an employee could be returned by this function multiple times.
 *
 * Another important property is the resultConceptItems from the ResultsTableData data structure. As you can see, it is an object. Every property
 * of this object represents a result concept item on a specific policy period. As you can see on the file resulsTableData.specs, it looks like this:
 *
 * {
 *    id: 1,
      paymentType: 'regular_payroll',
      employee: {
        id: 1,
        access: { id: 1,  fullName: 'Homer Simpson',}
      },
      resultConceptItems: {
        'conceptId:1:policyPeriodId:1': {
          amount: 123.43,
          conceptId: 1,
          conceptName: DEFAULT_RESULT_CONCEPT.gross_pay,
          default: true,
        },
        'conceptId:2:policyPeriodId:2': {
          amount: 1123.43,
          conceptId: 2,
          conceptName: DEFAULT_RESULT_CONCEPT.net_pay,
          default: true,
        },
         'conceptId:2:policyPeriodId:1': {
          amount: 1123.43,
          conceptId: 2,
          conceptName: DEFAULT_RESULT_CONCEPT.net_pay,
          default: true,
        }
      },
      createdAt: '2023-01-01',
 * }
 *
 * As you can see, the resultConceptItems contains two properties: 'conceptId:2:policyPeriod:1' which contains information about the net salary on policy period 1, and 'conceptId:2:policyPeriod:2' which contains
 * information about the result concept on policy period 2.
 *
 * Having the result concept items like an object instead of an array has a reason: it makes easier to get the amount of a result concept item from a specific period. We need that
 * in order to show the difference between the amount of the current policy period and the previous policy period.
 */
export function convertResultsToResultsTableData(
  resultConceptItems: ResultConceptItemTableRowFragment[],
  results: Results
): ResultsTableData {
  const tableDataGroupByEmployeeAndPaymentType = resultConceptItems.reduce(
    (tableData, resultConceptItem) => {
      const { employee, concept, id, payrollResultId, amount, createdAt } = resultConceptItem
      const payrollResult = results.get(payrollResultId)

      if (!employee || !concept || !payrollResult) {
        return tableData
      }

      const paymentType = payrollResult.get('concept') ?? ResultConcepts.regular_payroll
      const policyPeriodId = payrollResult.get('payroll_policy_period_id')
      const groupByKey = `${employee.id}:${paymentType}`
      const resultConceptItemKey = getResultConceptItemHash(concept.id, policyPeriodId)

      if (groupByKey in tableData) {
        tableData[groupByKey]!.conceptItems[resultConceptItemKey] = {
          conceptId: concept.id,
          conceptName: concept.name,
          amount: amount ?? null,
          default: concept.default,
          policyPeriodId,
        }
      } else {
        tableData[groupByKey] = {
          id,
          employee,
          paymentType,
          conceptItems: {
            [resultConceptItemKey]: {
              conceptId: concept.id,
              conceptName: concept.name,
              amount: amount ?? null,
              default: concept.default,
              policyPeriodId,
            },
          },
          createdAt,
        }
      }

      return tableData
    },
    {} as { [uniqueKey: string]: ResultRowData }
  )

  return Object.values(tableDataGroupByEmployeeAndPaymentType)
}

export function convertEmployeeResultsSummariesToResultsTableData(
  employeeResultsSummaries: PayrollEmployeeResultsSummary[],
  results: Results
): ResultsTableData {
  const tableDataGroupByEmployeeAndPaymentType = employeeResultsSummaries.reduce(
    (tableData, summary) => {
      const {
        employee,
        payType: paymentType,
        resultConceptItems,
        payrollResultId,
        ...summaryExceptPayTypeAndResultConceptItems
      } = summary
      const payrollResult = results.get(payrollResultId)

      if (!employee || !paymentType || !payrollResultId) {
        return tableData
      }

      const policyPeriodId = payrollResult?.get('payroll_policy_period_id')
      const groupByKey = `${employee.id}:${paymentType}`
      const firstConceptItem = resultConceptItems[0]

      // Builds conceptItems from resultConceptItems
      const conceptItems = resultConceptItems.reduce((itemsByKey, resultConceptItem) => {
        const key = getResultConceptItemHash(resultConceptItem.concept.id, policyPeriodId)
        return {
          ...itemsByKey,
          [key]: resultConceptItem,
        }
      }, {})

      if (!(groupByKey in tableData)) {
        tableData[groupByKey] = {
          id: firstConceptItem!.id,
          createdAt: firstConceptItem!.createdAt,
          employee: summary.employee,
          paymentType: paymentType as ResultConceptsType,
          conceptItems,
        }
      } else {
        tableData[groupByKey]!.conceptItems = {
          ...tableData[groupByKey]!.conceptItems,
          ...conceptItems,
        }
      }

      return tableData
    },
    {} as { [uniqueKey: string]: ResultRowData }
  )

  return Object.values(tableDataGroupByEmployeeAndPaymentType)
}

/**
 * Based on a result row data, it retuns the payment status. To make this calculation, we are checking if
 * there is any payment associated with the employee and the payment type from the result row data.
 *
 * The checks evaluated are:
 *
 * - When the employee does not have any payment of a result payment type, then we consider that the employee as unpaid.
 * - When the employee result payment type amount is lower than the value from the amount paid, then we consider that the employee is partially paid.
 * - When the employee result payment type amount is higher than the value from the amount paid, then we consider that the employee is overpaid.
 * - When the employee result payment type amount equals to the value from the amount paid, then we consider that the employee is paid.
 *
 * For making these calculations, we are just taking into account the net salary concept from the results
 */
export function getResultRowDataPaymentStatus(
  resultRowData: ResultRowData,
  payments: Payments,
  policyPeriodId: number,
  netSalaryConceptId: number,
  resultConceptType: ResultConceptsType
): PaymentStatus {
  const netSalaryResultConceptItem = getResultConceptItem(
    resultRowData,
    netSalaryConceptId,
    policyPeriodId
  )

  if (!netSalaryResultConceptItem || netSalaryResultConceptItem.amount === null) {
    return NOT_PAID
  }

  const netSalaryAmount = netSalaryResultConceptItem.amount
  const employeePaymentItems = payments
    .getByConcept(policyPeriodId, resultConceptType)
    .flatMap((payment) => payment.employeePaymentItems(resultRowData.employee.id))
  const employeePaidPaymentItems = paymentItemLib.getPaidPaymentItems(
    employeePaymentItems,
    payments
  )

  if (employeePaidPaymentItems.length === 0) {
    return NOT_PAID
  }

  const totalAmountPaid = sumBy(
    employeePaidPaymentItems,
    (paymentItem) => paymentItem.attributes.amount_in_cents
  )

  if (totalAmountPaid > netSalaryAmount) {
    return OVERPAID
  }

  if (totalAmountPaid < netSalaryAmount) {
    return PARTIALLY_PAID
  }

  return PAID
}

/**
 * It returns the payment status based on multiple result rows data. A use case is to show the payment status for a specific employee with several result rows.
 */
export function getResultRowsDataPaymentStatus(
  resultRowsData: ResultRowData[],
  payments: Payments,
  policyPeriodId: number,
  netSalaryConceptId: number
): PaymentStatus {
  const paymentStatuses = resultRowsData.map((resultRowData) =>
    getResultRowDataPaymentStatus(
      resultRowData,
      payments,
      policyPeriodId,
      netSalaryConceptId,
      resultRowData.paymentType
    )
  )

  if (paymentStatuses.length === 0) {
    return NOT_PAID
  }

  if (paymentStatuses.every((paymentStatus) => paymentStatus === NOT_PAID)) {
    return NOT_PAID
  }

  if (paymentStatuses.every((paymentStatus) => paymentStatus === PAID)) {
    return PAID
  }

  if (
    paymentStatuses.every((paymentStatus) => paymentStatus === PAID || paymentStatus === OVERPAID)
  ) {
    return OVERPAID
  }

  return PARTIALLY_PAID
}

/**
 * It returns all result rows data of an employee.
 *
 */
export function getResultRowsDataByEmployee(resultDataRows: ResultRowData[], employeeId?: number) {
  return employeeId
    ? resultDataRows.filter((resultDataRow) => resultDataRow.employee?.id === employeeId)
    : []
}

/**
 * Returns the total sum of all the result rows data amounts of a concept.
 *
 */
export function sumResultRowsDataByConcept(
  resultRowsData: ResultRowData[],
  conceptId: number,
  policyPeriodId: number
): number {
  const resultConceptItemsByConceptName = resultRowsData.reduce(
    (resultConceptItems, resultRowData) => {
      const resultConceptItem = getResultConceptItem(resultRowData, conceptId, policyPeriodId)

      return resultConceptItem ? [...resultConceptItems, resultConceptItem] : resultConceptItems
    },
    [] as ResultConceptItemTableData[]
  )

  return sumBy(
    resultConceptItemsByConceptName,
    (resultConceptItem) => resultConceptItem.amount ?? 0
  )
}

/**
 * It returns the differenct between two result concept item amounts. This is useful to compare result concept items from
 * different policy periods.
 *
 */
export function getResultRowDataDiffAmountInCents(
  resultRowData: ResultRowData,
  conceptId: number,
  currentPolicyPeriodId: number,
  otherPolicyPeriodId: number
): number | null {
  const currentPeriodAmount =
    getResultConceptItem(resultRowData, conceptId, currentPolicyPeriodId)?.amount ?? null
  const otherPeriodAmount =
    getResultConceptItem(resultRowData, conceptId, otherPolicyPeriodId)?.amount ?? null

  return currentPeriodAmount !== null && otherPeriodAmount !== null
    ? currentPeriodAmount - otherPeriodAmount
    : null
}

/**
 * A result concept item hash identifies each result concept item on the result table data. It includes the payroll concept name
 * and the policy period id, so it is easier to compare two result concept items from different policy periods
 */
function getResultConceptItemHash(conceptId: number, policyPeriodId: number): string {
  return `conceptId:${conceptId}:policyPeriodId:${policyPeriodId}`
}

export function getResultConceptItem(
  resultRowData: ResultRowData,
  conceptId: number,
  policyPeriodId: number
): ResultConceptItemTableData | null {
  return resultRowData.conceptItems[getResultConceptItemHash(conceptId, policyPeriodId)] ?? null
}
